import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight, ArrowLeft } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { suburbRegions, allSuburbs } from "../suburbs";

const ServiceAreas = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const title = "Service Areas | Premium House Cleaning Across Brisbane | The Sterling Touch";
  const description = `The Sterling Touch services ${allSuburbs.length} premium suburbs across Brisbane — from Pullenvale and Fig Tree Pocket to Ascot, Bulimba and Paddington. Refined, discreet, fully insured.`;

  return (
    <div className="min-h-screen bg-cream">
      <SEO
        title={title}
        description={description}
        path="/cleaning"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Service Areas", path: "/cleaning" },
        ]}
      />
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 bg-white border-b border-[#ead8c9]">
          <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-ink/60 hover:text-ink mb-8 transition-colors"
            >
              <ArrowLeft size={14} /> Back to home
            </Link>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#faf3eb] border border-[#e8d9cc] text-xs uppercase tracking-[0.2em] text-[#daae9c]">
              <MapPin size={14} className="text-sterling-dark" />
              Service Areas
            </div>
            <h1 className="font-serif-display text-5xl md:text-6xl lg:text-7xl text-ink mt-6 leading-[1.05] text-balance">
              Premium cleaning across{" "}
              <span className="italic text-[#daae9c]">Brisbane.</span>
            </h1>
            <p className="mt-6 text-lg text-ink/70 max-w-2xl mx-auto leading-relaxed">
              The Sterling Touch services 20+ carefully selected suburbs across Brisbane.
            </p>
          </div>
        </section>

        {/* Suburbs grouped by region */}
        <section className="py-16 lg:py-24 bg-cream">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 space-y-16">
            {suburbRegions.map((region) => (
              <div key={region.region}>
                <div className="mb-8 max-w-2xl">
                  <div className="text-xs uppercase tracking-[0.25em] text-[#daae9c]">
                    Region
                  </div>
                  <h2 className="font-serif-display text-3xl md:text-4xl text-ink mt-2">
                    {region.region}
                  </h2>
                  <p className="mt-3 text-ink/70 leading-relaxed">
                    {region.intro}
                  </p>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {region.suburbs.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/cleaning/${s.slug}`}
                      className="group p-6 bg-white rounded-2xl border border-[#ead8c9] hover:border-sterling-dark transition-all hover:-translate-y-1 hover:shadow-[0_20px_40px_-20px_rgba(138,93,72,0.2)]"
                    >
                      <div className="flex items-center justify-between">
                        <div className="w-10 h-10 rounded-xl bg-[#faf3eb] group-hover:bg-sterling flex items-center justify-center transition-colors">
                          <MapPin size={16} className="text-sterling-dark" />
                        </div>
                        <ArrowRight
                          size={16}
                          className="text-ink/40 group-hover:text-ink group-hover:translate-x-0.5 transition-all"
                        />
                      </div>
                      <div className="font-serif-display text-2xl text-ink mt-4">
                        {s.name}
                      </div>
                      <div className="mt-4 text-xs uppercase tracking-[0.18em] text-[#daae9c] group-hover:text-sterling-dark transition-colors">
                        Cleaning in {s.name} →
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-white border-t border-[#ead8c9]">
          <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
            <h2 className="font-serif-display text-3xl md:text-4xl text-ink leading-tight">
              Don’t see your suburb?
            </h2>
            <p className="mt-3 text-ink/70">
              We service many surrounding pockets too — get in touch and we’ll
              confirm availability in your area.
            </p>
            <Link to="/quote">
              <span className="inline-flex items-center gap-2 mt-6 px-6 h-12 rounded-full bg-[#daae9c] hover:bg-white text-ink border border-transparent hover:border-[#daae9c] transition-colors text-sm transition-colors">
                Request a personalised quote
                <ArrowRight size={14} />
              </span>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ServiceAreas;
